//
// # IvaErrorRow
//
// Single error row in the IVA panel.
//

import React from "react";
import {
  Box,
  CircleSlash,
  Pin as PinIcon,
  Route as RouteIcon,
  Layers,
} from "lucide-react";
import type { IvaError, IvaErrorKind } from "./types";

interface IvaErrorRowProps {
  error: IvaError;
  index: number;
}

const errorStyles: Record<
  IvaErrorKind,
  { icon: React.ReactNode; bgClass: string; iconClass: string; label: string }
> = {
  instance_outside_outline: {
    icon: <Box size={12} />,
    bgClass: "bg-orange-500/10 hover:bg-orange-500/20",
    iconClass: "text-orange-400",
    label: "Instance outside outline",
  },
  missing_outline: {
    icon: <CircleSlash size={12} />,
    bgClass: "bg-yellow-500/10 hover:bg-yellow-500/20",
    iconClass: "text-yellow-400",
    label: "Missing outline",
  },
  route_outside_blockage: {
    icon: <RouteIcon size={12} />,
    bgClass: "bg-red-500/10 hover:bg-red-500/20",
    iconClass: "text-red-400",
    label: "Route outside blockage",
  },
  instance_blockage_outside_parent_blockage: {
    icon: <Layers size={12} />,
    bgClass: "bg-red-500/10 hover:bg-red-500/20",
    iconClass: "text-red-400",
    label: "Instance blockage not covered",
  },
  pin_not_on_blockage_boundary: {
    icon: <PinIcon size={12} />,
    bgClass: "bg-purple-500/10 hover:bg-purple-500/20",
    iconClass: "text-purple-400",
    label: "Pin not on blockage boundary",
  },
  non_convex_blockages_on_layer: {
    icon: <CircleSlash size={12} />,
    bgClass: "bg-yellow-500/10 hover:bg-yellow-500/20",
    iconClass: "text-yellow-400",
    label: "Non-convex blockages on layer",
  },
};

function formatLabel(error: IvaError): string {
  if (error.instance) return error.instance;
  if (error.pin) return error.pin;
  return errorStyles[error.kind].label;
}

function formatSubtext(error: IvaError): string {
  const bits: string[] = [];
  if (error.layer !== undefined) bits.push(`layer ${error.layer}`);
  bits.push(error.message);
  return bits.join(" — ");
}

export function IvaErrorRow({ error, index }: IvaErrorRowProps) {
  void index;
  const style = errorStyles[error.kind];

  return (
    <div
      className={`w-full px-2 py-1.5 rounded flex items-center gap-2 text-left ${style.bgClass}`}
    >
      <span className={style.iconClass}>{style.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-medium truncate">
          {formatLabel(error)}
        </div>
        <div className="text-[10px] text-[var(--color-ide-text-dim)] truncate">
          {formatSubtext(error)}
        </div>
      </div>
    </div>
  );
}
